/**
 * Initial of a full name, used for avatar fallback.
 */
export function initials(name) {
    if (!name) return "?";
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || "")
        .join("");
}

// All PREREC datetimes are rendered in Europe/Paris — see lib/tz.js.
import { COMPANY_TZ } from "./tz";

export function formatDate(date) {
    if (!date) return "—";
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            timeZone: COMPANY_TZ,
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(new Date(date));
    } catch {
        return "—";
    }
}

/**
 * Human-readable label for a rejection reason code.
 * Shared with the candidate list card + the Refuses page + CSV export.
 */
export function rejectionReasonLabel(reason) {
    const map = {
        criteres_non_remplis: "Critères non remplis",
        candidat_indisponible: "Candidat indisponible",
        numero_injoignable: "Numéro injoignable",
        immersion_failed: "Échec immersion",
        immersion_no_show: "No-show immersion",
        other: "Autre",
    };
    return map[reason] || reason || "—";
}

export function formatDateTime(date) {
    if (!date) return "—";
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            timeZone: COMPANY_TZ,
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(date));
    } catch {
        return "—";
    }
}

/**
 * "il y a 12 min" / "il y a 2 h" / "il y a 3 j" / "à l'instant"
 */
export function timeAgo(date) {
    if (!date) return "—";
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return "à l'instant";
    const intervals = [
        { label: "an", seconds: 31536000 },
        { label: "mois", seconds: 2592000 },
        { label: "j", seconds: 86400 },
        { label: "h", seconds: 3600 },
        { label: "min", seconds: 60 },
    ];
    for (const i of intervals) {
        const count = Math.floor(seconds / i.seconds);
        if (count >= 1) {
            const space = i.label === "an" || i.label === "mois" ? " " : "";
            return `il y a ${count}${space}${i.label}`;
        }
    }
    return "à l'instant";
}

/**
 * Real-time signal for a candidate row.
 * Returns { label, tone } where tone is one of: ok | warn | alert | info
 */
export function liveSignal(candidate) {
    if (!candidate?.created_at) return null;
    const now = Date.now();
    const created = new Date(candidate.created_at).getTime();
    const updated = candidate.updated_at ? new Date(candidate.updated_at).getTime() : created;
    const hoursSinceCreated = (now - created) / 3600000;
    const hoursSinceUpdated = (now - updated) / 3600000;

    // New high-priority candidates : received recently
    if (candidate.status === "new" && hoursSinceCreated < 1) {
        return { label: "Reçu < 1 h", tone: "info" };
    }
    if (candidate.status === "new" && hoursSinceCreated < 24) {
        return { label: `Reçu ${timeAgo(candidate.created_at).replace("il y a ", "")}`, tone: "info" };
    }

    // Stale candidates : assigned but no recent action
    if (
        ["prerec_assigned", "new"].includes(candidate.status) &&
        hoursSinceUpdated >= 24
    ) {
        return { label: "Sans contact 24 h+", tone: "alert" };
    }
    if (
        ["prerec_assigned"].includes(candidate.status) &&
        hoursSinceUpdated >= 4
    ) {
        return { label: `À rappeler · ${timeAgo(candidate.updated_at).replace("il y a ", "")}`, tone: "warn" };
    }

    if (candidate.status === "immersion_scheduled") {
        return { label: "Immersion à venir", tone: "info" };
    }
    if (candidate.status === "immersion_done") {
        return { label: "À valider", tone: "warn" };
    }
    if (candidate.status === "validated") {
        return { label: "Validé", tone: "ok" };
    }
    if (candidate.status === "onboarding") {
        return { label: "Onboarding", tone: "ok" };
    }
    if (candidate.status === "rejected") {
        return { label: "Refusé", tone: "alert" };
    }
    return { label: `Reçu ${timeAgo(candidate.created_at).replace("il y a ", "")}`, tone: "info" };
}

/**
 * Convert French phone "06 12 34 56 78" → "+33612345678" for WhatsApp / tel:.
 * Leaves anything starting with + intact, only trims spaces.
 */
export function toE164(phone) {
    if (!phone) return "";
    const cleaned = String(phone).replace(/[\s.\-()]/g, "");
    if (cleaned.startsWith("+")) return cleaned;
    if (cleaned.startsWith("0033")) return "+" + cleaned.slice(2);
    if (cleaned.startsWith("33")) return "+" + cleaned;
    if (cleaned.startsWith("0")) return "+33" + cleaned.slice(1);
    return "+" + cleaned;
}
